"use client";

import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef, type ReactNode } from "react";

/**
 * Reveal-on-scroll wrapper: a clean slide-up-and-fade-in (24px rise, 0.45s
 * ease-out) that fires once per page load as the element enters the viewport
 * — never re-triggers on scroll back. `index` staggers siblings ~90ms apart
 * so grids arrive in sequence rather than popping in at once.
 *
 * Transform + opacity only, so the element's final layout space is reserved
 * from first paint — no layout shift. prefers-reduced-motion renders content
 * at rest immediately (framer animations are JS-driven, so the global CSS
 * reduced-motion rule alone would not stop them).
 */
export function Reveal({
  children,
  index = 0,
  className,
}: {
  children: ReactNode;
  index?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-12% 0px -12% 0px" });
  const reduce = useReducedMotion();

  // The motion.div must stay mounted in BOTH modes: server HTML carries the
  // hidden initial style, and React hydration never reconciles attribute
  // differences — only a mounted framer component reliably clears it. Under
  // reduced motion we therefore skip via props (jump straight to rest, zero
  // duration) rather than by swapping in a plain <div>.
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduce ? false : { opacity: 0, y: 24 }}
      animate={reduce || inView ? { opacity: 1, y: 0 } : {}}
      transition={
        reduce
          ? { duration: 0 }
          : { duration: 0.45, ease: "easeOut", delay: index * 0.09 }
      }
    >
      {children}
    </motion.div>
  );
}
